// SystemSignalsModal — admin-only data freshness control panel.
//
// Centered popup over a blurred backdrop. Renders a single fetch from
// the system-health Lambda into 3 RAG cards + 4 DB cards + 1 Code card.
// Status thresholds (per spec):
//   green  : lag < 2hr AND no errors
//   orange : lag 2–24hr (no errors)
//   red    : lag > 24hr OR any errors in the 7-day window
//
// v1 ships the cards + thresholds + click-out close. v2 adds per-
// Fireflies-identity sub-list, hover glow, auto-refresh, dot pulse.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const SANS = 'Inter, -apple-system, BlinkMacSystemFont, system-ui, sans-serif';
const MONO = "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace";

const URL_BASE = import.meta.env.VITE_AWS_SYSTEM_HEALTH_URL || '';

// ── Threshold helpers ───────────────────────────────────────────────
function lagToStatus(lagMinutes, errors = 0) {
  if (errors > 0) return 'red';
  if (lagMinutes == null) return 'mute';
  if (lagMinutes > 24 * 60) return 'red';
  if (lagMinutes > 2 * 60) return 'orange';
  return 'green';
}

function dbStatus(lastUpdatedIso) {
  if (!lastUpdatedIso) return 'mute';
  const lagMin = (Date.now() - new Date(lastUpdatedIso).getTime()) / 60000;
  if (lagMin > 7 * 24 * 60) return 'red';
  if (lagMin > 24 * 60)     return 'orange';
  return 'green';
}

// S3 nightly-pull threshold — looser than RAG since the cadence is
// daily, not minute-level. Fresh < 30hr, warning 30–72hr, stale > 72hr.
function s3DatasetStatus(lastModifiedIso) {
  if (!lastModifiedIso) return 'mute';
  const lagHrs = (Date.now() - new Date(lastModifiedIso).getTime()) / 3600000;
  if (lagHrs > 72) return 'red';
  if (lagHrs > 30) return 'orange';
  return 'green';
}

const STATUS_COLOR = {
  green:  '#3DD68C',
  orange: '#E6A23A',
  red:    '#F2655A',
  mute:   'rgba(255,255,255,0.25)',
};

function fmtRelative(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `~${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `~${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return d.toLocaleDateString(undefined, { month: 'short', day: '2-digit' });
}

function fmtLag(min) {
  if (min == null) return '—';
  if (min < 60) return `~${min} min`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `~${hrs} hr`;
  return `~${Math.floor(hrs / 24)}d`;
}

function fmtNumber(n) {
  if (n == null) return '—';
  return new Intl.NumberFormat('en-US').format(n);
}

// ── Card primitives ─────────────────────────────────────────────────
function StatusDot({ status, size = 8 }) {
  // Pulsing dot for green/red — calm enough not to distract, present
  // enough to read as alive at a glance. Orange + mute don't pulse so
  // a busy panel doesn't feel like a Christmas tree.
  const shouldPulse = status === 'green' || status === 'red';
  return (
    <span
      className={shouldPulse ? `sig-pulse sig-pulse-${status}` : ''}
      style={{
        width: size, height: size, borderRadius: 999,
        background: STATUS_COLOR[status] || STATUS_COLOR.mute,
        flexShrink: 0,
      }}
    />
  );
}

function StatLine({ label, value, mono = true, danger = false }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
      gap: 12, padding: '4px 0',
    }}>
      <span style={{ fontFamily: SANS, fontSize: 11, color: 'rgba(255,255,255,0.45)', letterSpacing: '-0.005em' }}>{label}</span>
      <span style={{
        fontFamily: mono ? MONO : SANS,
        fontSize: 12,
        color: danger ? '#F2655A' : 'rgba(255,255,255,0.92)',
        fontWeight: danger ? 600 : 500,
      }}>{value}</span>
    </div>
  );
}

function Card({ name, status, children, kicker }) {
  // Hover glow: border + subtle outer shadow tinted by status color.
  // Mute status falls back to a neutral white tint so empty cards
  // still feel touched, just not signalled.
  const glow =
    status === 'green'  ? 'rgba(61,214,140,'  :
    status === 'orange' ? 'rgba(230,162,58,'  :
    status === 'red'    ? 'rgba(242,101,90,'  :
                          'rgba(255,255,255,';
  return (
    <div
      className={`sig-card sig-card-${status}`}
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 10,
        padding: '14px 16px',
        display: 'flex', flexDirection: 'column', gap: 6,
        animation: 'sigCardIn 180ms ease-out both',
        // CSS custom props feed the :hover rule injected in the modal.
        ['--sig-glow-color']: `${glow}0.45)`,
        ['--sig-glow-shadow']: `${glow}0.18)`,
        transition: 'border-color 160ms, box-shadow 160ms, transform 160ms',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <StatusDot status={status} />
        <span style={{ fontFamily: SANS, fontSize: 13, fontWeight: 600, color: '#fff', letterSpacing: '-0.005em' }}>{name}</span>
        {kicker && (
          <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 10, color: 'rgba(255,255,255,0.4)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {kicker}
          </span>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
}

function RagCard({ name, data }) {
  const status = lagToStatus(data?.lag_minutes, data?.errors);
  return (
    <Card name={name} status={status}>
      <StatLine label="Last updated" value={fmtRelative(data?.last_ingested)} />
      <StatLine label="Depth"        value={`${fmtNumber(data?.chunks)} chunks`} />
      <StatLine label="Lag"          value={fmtLag(data?.lag_minutes)} />
      <StatLine label="Pipeline"     value={`${fmtNumber(data?.runs)} runs`} />
      <StatLine label="Errors"       value={fmtNumber(data?.errors)} danger={data?.errors > 0} />
      {data?.latest_error && <WhyLine label="Why" detail={data.latest_error.detail} when={data.latest_error.at} />}
    </Card>
  );
}

// Compact one-line "Why is this red?" disclosure shown beneath the
// stat lines. Truncates to 2 lines via webkit clamp; tooltip reveals
// the full text on hover. When `when` is set, surfaces a relative
// timestamp on the right.
function WhyLine({ label, detail, when }) {
  if (!detail) return null;
  return (
    <div style={{
      marginTop: 8, paddingTop: 8,
      borderTop: '1px solid rgba(242,101,90,0.12)',
    }} title={detail}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontFamily: SANS, fontSize: 10.5, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#F2655A', fontWeight: 600 }}>
          {label}
        </span>
        {when && (
          <span style={{ marginLeft: 'auto', fontFamily: MONO, fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
            {fmtRelative(when)}
          </span>
        )}
      </div>
      <div style={{
        marginTop: 3, fontFamily: MONO, fontSize: 11, color: 'rgba(255,255,255,0.78)',
        lineHeight: 1.4,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {detail}
      </div>
    </div>
  );
}

// Per-identity row inside the Fireflies card — same threshold logic
// as the source-level status, applied to each speaker's most recent
// transcript ingest. Mo's spec format: "Mo → ~23h", "Jade → 10d ⚠️".
function IdentityRow({ identity }) {
  const lastIso = identity?.last;
  const lagMin = lastIso ? Math.max(0, (Date.now() - new Date(lastIso).getTime()) / 60000) : null;
  const status = lagToStatus(lagMin);
  const label =
    lagMin == null              ? '—'             :
    lagMin < 60                 ? `~${Math.floor(lagMin)}m` :
    lagMin < 24 * 60            ? `~${Math.floor(lagMin / 60)}h` :
                                  `${Math.floor(lagMin / 60 / 24)}d`;
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      padding: '3px 0',
      fontFamily: SANS, fontSize: 11.5,
    }}>
      <StatusDot status={status} size={6} />
      <span style={{ color: 'rgba(255,255,255,0.78)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {identity?.name || '—'}
      </span>
      <span style={{ fontFamily: MONO, fontSize: 11, color: status === 'red' ? '#F2655A' : status === 'orange' ? '#E6A23A' : 'rgba(255,255,255,0.7)' }}>
        {label}
      </span>
    </div>
  );
}

// Fireflies-specific card — same top-level stats as RagCard, plus a
// compact per-identity sub-list ("Per-identity freshness") so admins
// can spot a single speaker whose pipeline has stalled even when the
// source-level lag still reads green.
function FirefliesCard({ data }) {
  const status = lagToStatus(data?.lag_minutes, data?.errors);
  const identities = (data?.identities || []).slice(0, 8);
  return (
    <Card name="Fireflies" status={status}>
      <StatLine label="Last updated" value={fmtRelative(data?.last_ingested)} />
      <StatLine label="Depth"        value={`${fmtNumber(data?.chunks)} chunks`} />
      <StatLine label="Lag"          value={fmtLag(data?.lag_minutes)} />
      <StatLine label="Pipeline"     value={`${fmtNumber(data?.runs)} runs`} />
      <StatLine label="Errors"       value={fmtNumber(data?.errors)} danger={data?.errors > 0} />
      {data?.latest_error && <WhyLine label="Why" detail={data.latest_error.detail} when={data.latest_error.at} />}
      {identities.length > 0 && (
        <div style={{
          marginTop: 8, paddingTop: 8,
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{
            fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)',
            marginBottom: 4,
          }}>Per-identity freshness</div>
          {identities.map(id => <IdentityRow key={id.name} identity={id} />)}
        </div>
      )}
    </Card>
  );
}

function DbCard({ name, data }) {
  const status = dbStatus(data?.last_updated);
  return (
    <Card name={name} status={status} kicker="DB">
      <StatLine label="Last updated" value={fmtRelative(data?.last_updated)} />
    </Card>
  );
}

function DatasetCard({ name, data }) {
  const status = s3DatasetStatus(data?.last_modified);
  // Pull the partition date out of paths like "dt=2026-04-24/data.jsonl"
  // — when it doesn't match, fall back to the whole tail.
  const partitionLabel = (() => {
    const tail = data?.latest_partition;
    if (!tail) return null;
    const m = String(tail).match(/dt=([0-9-]+)/);
    return m ? `dt=${m[1]}` : tail.split('/')[0];
  })();
  return (
    <Card name={name} status={status} kicker="S3">
      <StatLine label="Last modified" value={fmtRelative(data?.last_modified)} />
      <StatLine label="Partitions"    value={fmtNumber(data?.key_count)} />
      {partitionLabel && <StatLine label="Latest" value={partitionLabel} />}
      {data?.error && <WhyLine label="Why" detail={String(data.error)} />}
      {!data?.error && status === 'red' && data?.last_modified && (
        <WhyLine
          label="Why"
          detail={`No new partitions since ${partitionLabel || 'the last write'}. Upstream pipeline appears to have stopped writing this prefix.`}
        />
      )}
    </Card>
  );
}

function CodeCard({ data }) {
  const status = data?.time
    ? (Date.now() - new Date(data.time).getTime() > 30 * 86400000 ? 'orange' : 'green')
    : 'mute';
  return (
    <Card name="GitHub · main" status={status} kicker="CODE">
      <StatLine label="Last commit" value={data?.last_commit || '—'} />
      <StatLine label="When"        value={fmtRelative(data?.time)} />
      <div style={{
        marginTop: 4, fontFamily: SANS, fontSize: 11.5,
        color: 'rgba(255,255,255,0.65)', lineHeight: 1.4,
        overflow: 'hidden', textOverflow: 'ellipsis',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
      }} title={data?.message || ''}>
        {data?.message || '—'}
      </div>
    </Card>
  );
}

// ── Modal shell ─────────────────────────────────────────────────────
export default function SystemSignalsModal({ session, onClose }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchHealth = useCallback(async () => {
    if (!URL_BASE) {
      setError('VITE_AWS_SYSTEM_HEALTH_URL is not configured.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(URL_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userPin: session?.code }),
      });
      const j = await res.json();
      if (!res.ok) {
        setError(j?.error || `Error ${res.status}`);
        setData(null);
      } else {
        setData(j);
      }
    } catch (e) {
      setError(e?.message || 'Network error');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [session?.code]);

  useEffect(() => { fetchHealth(); }, [fetchHealth]);

  // ESC to close + lock body scroll while open.
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  // Auto-refresh every 30s while the modal is open AND the tab is
  // visible. Pausing on hidden saves the Lambda cycle when the user
  // task-switches away — prevents idle 30s ticks accumulating across
  // unread tabs, and resumes on visibility change.
  useEffect(() => {
    if (!URL_BASE) return undefined;
    let timer = null;
    function start() {
      if (timer) return;
      timer = setInterval(() => {
        if (!document.hidden) fetchHealth();
      }, 30000);
    }
    function stop() {
      if (timer) { clearInterval(timer); timer = null; }
    }
    function onVisibility() {
      if (document.hidden) stop(); else start();
    }
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [fetchHealth]);

  const aggregate = data?.status || (loading ? 'mute' : 'red');
  const aggregateLabel =
    aggregate === 'healthy'  ? 'Healthy' :
    aggregate === 'degraded' ? 'Degraded' :
    aggregate === 'issues'   ? 'Issues detected' :
    aggregate === 'mute'     ? '—' :
                                'Issues detected';
  const aggregateDot =
    aggregate === 'healthy'  ? 'green' :
    aggregate === 'degraded' ? 'orange' :
    aggregate === 'issues'   ? 'red' :
                                'mute';

  return createPortal(
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(8,10,14,0.62)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        animation: 'sigOverlayIn 160ms ease-out both',
      }}
    >
      <style>{`
        @keyframes sigOverlayIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes sigCardIn { from { opacity: 0; transform: translateY(4px) } to { opacity: 1; transform: translateY(0) } }
        @keyframes sigPulseGreen {
          0%, 100% { box-shadow: 0 0 0 0 rgba(61,214,140,0.55); }
          50%      { box-shadow: 0 0 0 4px rgba(61,214,140,0);    }
        }
        @keyframes sigPulseRed {
          0%, 100% { box-shadow: 0 0 0 0 rgba(242,101,90,0.55); }
          50%      { box-shadow: 0 0 0 4px rgba(242,101,90,0);    }
        }
        .sig-pulse { will-change: box-shadow; }
        .sig-pulse-green { animation: sigPulseGreen 2.4s ease-in-out infinite; }
        .sig-pulse-red   { animation: sigPulseRed   1.6s ease-in-out infinite; }
        .sig-card:hover {
          border-color: var(--sig-glow-color, rgba(255,255,255,0.18));
          box-shadow: 0 0 0 1px var(--sig-glow-color, transparent), 0 8px 24px -8px var(--sig-glow-shadow, transparent);
          transform: translateY(-1px);
        }
        @media (prefers-reduced-motion: reduce) {
          .sig-pulse-green, .sig-pulse-red { animation: none; }
          .sig-card:hover { transform: none; }
        }
      `}</style>
      <div
        role="dialog" aria-modal="true" aria-labelledby="sig-title"
        style={{
          width: 'min(960px, 100%)',
          maxHeight: '86vh',
          background: '#0F1217',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 14,
          boxShadow: '0 30px 80px -20px rgba(0,0,0,0.6)',
          display: 'flex', flexDirection: 'column',
          color: '#fff',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <header style={{
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '18px 22px', borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{ flex: 1 }}>
            <h2 id="sig-title" style={{
              margin: 0, fontFamily: SANS, fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em',
            }}>System Signals</h2>
            <div style={{
              fontFamily: SANS, fontSize: 11.5, color: 'rgba(255,255,255,0.45)', marginTop: 2,
            }}>
              Live data freshness + ingestion health
              {data?.window_hours ? ` · last ${data.window_hours}h` : ''}
            </div>
          </div>

          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 12px', borderRadius: 999,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
          }}>
            <StatusDot status={aggregateDot} />
            <span style={{ fontFamily: SANS, fontSize: 12, color: 'rgba(255,255,255,0.85)', fontWeight: 500 }}>
              {aggregateLabel}
            </span>
          </div>

          <button
            type="button"
            onClick={fetchHealth}
            disabled={loading}
            title="Refresh"
            style={{
              width: 30, height: 30, borderRadius: 8,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.6)', cursor: loading ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              opacity: loading ? 0.5 : 1,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16M3 12a9 9 0 0 1 15.5-6.3L21 8" />
              <path d="M21 3v5h-5M3 21v-5h5" />
            </svg>
          </button>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: 30, height: 30, borderRadius: 8,
              background: 'transparent', border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.6)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </header>

        {/* Body */}
        <div style={{ overflow: 'auto', padding: '20px 22px 24px' }}>
          {loading && !data && (
            <div style={{ fontFamily: SANS, fontSize: 13, color: 'rgba(255,255,255,0.5)', padding: '40px 0', textAlign: 'center' }}>
              Reading signals…
            </div>
          )}
          {error && (
            <div style={{
              fontFamily: SANS, fontSize: 13, color: '#F2655A',
              padding: '12px 16px', borderRadius: 8,
              background: 'rgba(242,101,90,0.08)', border: '1px solid rgba(242,101,90,0.2)',
              marginBottom: 16,
            }}>
              {error}
            </div>
          )}

          {data && (
            <>
              <SectionLabel>RAG · Signals</SectionLabel>
              <div style={{
                display: 'grid', gap: 12,
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                marginBottom: 22,
              }}>
                <RagCard name="Slack"     data={data.rag?.slack} />
                <RagCard name="Email"     data={data.rag?.email} />
                <FirefliesCard            data={data.rag?.fireflies} />
              </div>

              <SectionLabel>Structured DB</SectionLabel>
              <div style={{
                display: 'grid', gap: 12,
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                marginBottom: 22,
              }}>
                {Object.entries(data.db || {}).map(([key, value]) => (
                  <DbCard key={key} name={key} data={value} />
                ))}
              </div>

              {data.datasets && Object.keys(data.datasets).length > 0 && (
                <>
                  <SectionLabel>Nightly Datasets · S3</SectionLabel>
                  <div style={{
                    display: 'grid', gap: 12,
                    gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                    marginBottom: 22,
                  }}>
                    {Object.entries(data.datasets).map(([key, value]) => (
                      <DatasetCard key={key} name={value.label || key} data={value} />
                    ))}
                  </div>
                </>
              )}

              <SectionLabel>Codebase</SectionLabel>
              <div style={{
                display: 'grid', gap: 12,
                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
              }}>
                <CodeCard data={data.code} />
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: MONO, fontSize: 10, letterSpacing: '0.12em',
      textTransform: 'uppercase', color: 'rgba(255,255,255,0.45)',
      marginBottom: 10,
    }}>{children}</div>
  );
}
